import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { productsApi } from '../../services/apiService';
import { useCurrency } from '../../context/CurrencyContext';
import { API_BASE_URL } from '../../config/api';
import { readMobileBrowsingHistory } from '../../utils/mobileBrowsingHistory';
import './MobileBrowsingHistoryPage.css';

const PAGE_SIZE = 20;

const resolveImage = (src) => {
  if (!src || typeof src !== 'string') return null;
  if (src.startsWith('http')) return src;
  if (src.startsWith('/uploads')) return `${API_BASE_URL}${src}`;
  return `${API_BASE_URL}${src.startsWith('/') ? src : `/${src}`}`;
};

const EmptyIllustration = () => (
  <svg className="mbh-empty-illus" viewBox="0 0 120 100" fill="none" aria-hidden>
    <ellipse cx="60" cy="88" rx="40" ry="6" fill="currentColor" opacity="0.2" />
    <path d="M30 45 L30 75 L90 75 L90 45 L60 25 Z" stroke="currentColor" strokeWidth="2" fill="none" />
    <path d="M35 50 L85 50" stroke="currentColor" strokeWidth="1.5" opacity="0.5" />
    <circle cx="52" cy="58" r="8" fill="currentColor" opacity="0.35" />
    <path d="M48 72 Q60 62 72 72" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" />
  </svg>
);

const MobileBrowsingHistoryPage = () => {
  const navigate = useNavigate();
  const { formatPrice, formatPriceRange } = useCurrency();
  const [historyItems, setHistoryItems] = useState([]);
  const [recommended, setRecommended] = useState([]);
  const [page, setPage] = useState(1);
  const [loadingRec, setLoadingRec] = useState(false);

  useEffect(() => {
    setHistoryItems(readMobileBrowsingHistory());
  }, []);

  const loadRecommended = useCallback(async () => {
    setLoadingRec(true);
    try {
      const res = await productsApi.getAll({ limit: PAGE_SIZE, page: 1 });
      const list = Array.isArray(res) ? res : (res?.products || res?.data || []);
      setRecommended(list.slice(0, PAGE_SIZE));
      setPage(2);
    } catch {
      setRecommended([]);
    } finally {
      setLoadingRec(false);
    }
  }, []);

  useEffect(() => {
    loadRecommended();
  }, [loadRecommended]);

  const loadMore = useCallback(async () => {
    if (loadingRec) return;
    setLoadingRec(true);
    try {
      const res = await productsApi.getAll({ limit: PAGE_SIZE, page });
      const list = Array.isArray(res) ? res : (res?.products || res?.data || []);
      setRecommended((prev) => {
        const ids = new Set(prev.map((p) => p.id));
        return [...prev, ...list.filter((p) => !ids.has(p.id))];
      });
      setPage((p) => p + 1);
    } catch {
      /* ignore */
    } finally {
      setLoadingRec(false);
    }
  }, [loadingRec, page]);

  const getPrice = (p) => {
    const min = p?.priceMin ?? p?.price ?? null;
    const max = p?.priceMax ?? null;
    if (min !== null && max !== null && Number(min) !== Number(max)) return formatPriceRange(min, max);
    if (min !== null) return formatPrice(min);
    return null;
  };

  const renderProductCard = (product, isHistorySnapshot = false) => {
    const rawImg = isHistorySnapshot
      ? product.image
      : (product?.image || (Array.isArray(product?.images) ? product.images[0] : product?.images));
    const img = resolveImage(rawImg) || rawImg;
    const price = getPrice(product);
    const seller = isHistorySnapshot ? product.seller : (product.seller || {});
    const name = product.name || product.title;
    return (
      <button
        type="button"
        key={isHistorySnapshot ? `h-${product.id}-${product.viewedAt}` : product.id}
        className="mbh-card"
        onClick={() => navigate(`/mobile/product/${product.id}`)}
      >
        <div className="mbh-card-img-wrap">
          {img ? <img src={img} alt="" /> : <div style={{ width: '100%', height: '100%', background: '#eee' }} />}
        </div>
        <div className="mbh-card-body">
          <p className="mbh-card-name">{name}</p>
          {price && <p className="mbh-card-price">{price}</p>}
          <p className="mbh-card-meta">
            {product.moq != null && product.moq !== '' && <span>MOQ: {product.moq} · </span>}
            {seller?.yearsActive ? `${seller.yearsActive} yrs · ` : ''}
            {seller?.country || 'CN'}
          </p>
          {(product.soldCount || product.sold) != null && (
            <p className="mbh-card-meta">{Number(product.soldCount || product.sold).toLocaleString()} sold</p>
          )}
        </div>
      </button>
    );
  };

  const showEmpty = historyItems.length === 0;

  return (
    <div className="mbh-page">
      <header className="mbh-header">
        <button type="button" className="mbh-back" onClick={() => navigate(-1)} aria-label="Back">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <h1 className="mbh-title">Browsing history</h1>
      </header>

      {showEmpty ? (
        <div className="mbh-empty">
          <EmptyIllustration />
          <p className="mbh-empty-text">No browsing history yet</p>
        </div>
      ) : (
        <section className="mbh-history-block">
          <h2 className="mbh-history-title">Recently viewed</h2>
          <div className="mbh-history-grid">
            {historyItems.map((item) => renderProductCard(item, true))}
          </div>
        </section>
      )}

      <h2 className="mbh-section-title">Recommended for you</h2>
      <div className="mbh-grid">
        {recommended.map((p) => renderProductCard(p, false))}
      </div>
      {recommended.length > 0 && (
        <button
          type="button"
          className="mbh-loading"
          onClick={loadMore}
          disabled={loadingRec}
          style={{ border: 'none', background: 'transparent', width: '100%', cursor: loadingRec ? 'wait' : 'pointer' }}
        >
          {loadingRec ? 'Loading…' : 'Load more'}
        </button>
      )}
    </div>
  );
};

export default MobileBrowsingHistoryPage;
